<template>
    <!-- When in a group context (parent handles InputGroup), render icon addons separately -->
    <template v-if="inGroup && (hasIcon || hasIconRight)">
        <!-- Left icon addon -->
        <InputGroupAddon v-if="hasIcon">
            <i :class="iconLeft" />
        </InputGroupAddon>

        <!-- Input component -->
        <Password
            v-if="inputType === 'password'"
            v-bind="mappedProps"
            ref="input"
            :feedback="hasPasswordReveal"
            :toggle-mask="hasPasswordReveal"
            :pt="passwordPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </Password>

        <InputNumber
            v-else-if="inputType === 'number'"
            v-bind="mappedProps"
            ref="input"
            :pt="inputNumberPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </InputNumber>

        <Textarea
            v-else-if="inputType === 'textarea'"
            v-bind="mappedProps"
            ref="input"
            :auto-resize="!$attrs.rows"
            :pt="textareaPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </Textarea>

        <InputText v-else v-bind="mappedProps" ref="input" :pt="inputTextPt" @update:modelValue="handleModelUpdate">
            <slot />
        </InputText>

        <!-- Right icon addon -->
        <InputGroupAddon
            v-if="hasIconRight"
            :class="{ 'tw:cursor-pointer': isIconRightClickable }"
            @click="handleIconRightClick"
        >
            <i :class="iconRight" />
        </InputGroupAddon>
    </template>

    <!-- Standalone with icons - wrap in InputGroup -->
    <div v-else-if="!inGroup && (hasIcon || hasIconRight)" :class="{ 'tw:w-full': expanded }">
        <InputGroup>
            <!-- Left icon -->
            <InputGroupAddon v-if="hasIcon" @click="handleIconClick">
                <i :class="iconLeft" />
            </InputGroupAddon>

            <!-- Password input -->
            <Password
                v-if="inputType === 'password'"
                v-bind="mappedProps"
                ref="input"
                :feedback="hasPasswordReveal"
                :toggle-mask="hasPasswordReveal"
                :pt="passwordPt"
                @update:modelValue="handleModelUpdate"
            >
                <slot />
            </Password>

            <!-- Number input -->
            <InputText
                v-else-if="inputType === 'number'"
                v-bind="mappedProps"
                ref="input"
                type="number"
                :pt="inputNumberPt"
                @update:modelValue="handleModelUpdate"
            >
                <slot />
            </InputText>

            <!-- Textarea -->
            <Textarea
                v-else-if="inputType === 'textarea'"
                v-bind="mappedProps"
                ref="input"
                :auto-resize="!$attrs.rows"
                :pt="textareaPt"
                @update:modelValue="handleModelUpdate"
            >
                <slot />
            </Textarea>

            <!-- Text input (default) -->
            <InputText v-else v-bind="mappedProps" ref="input" :pt="inputTextPt" @update:modelValue="handleModelUpdate">
                <slot />
            </InputText>

            <!-- Right icon -->
            <InputGroupAddon v-if="hasIconRight" @click="handleIconRightClick">
                <i :class="iconRight" />
            </InputGroupAddon>
        </InputGroup>
        <small
            v-if="hasMaxLength"
            class="tw:text-gray-400 tw:text-xs tw:block tw:text-right tw:mt-1"
        >{{ currentLength }}/{{ maxLength }}</small>
    </div>

    <!-- No icons - render input directly -->
    <template v-else>
        <!-- Password input -->
        <Password
            v-if="inputType === 'password'"
            v-bind="mappedProps"
            ref="input"
            :feedback="hasPasswordReveal"
            :toggle-mask="hasPasswordReveal"
            :pt="passwordPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </Password>

        <!-- Number input -->
        <InputText
            v-else-if="inputType === 'number'"
            v-bind="mappedProps"
            ref="input"
            type="number"
            :pt="inputNumberPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </InputText>

        <!-- Textarea -->
        <Textarea
            v-else-if="inputType === 'textarea'"
            v-bind="mappedProps"
            ref="input"
            :auto-resize="!$attrs.rows"
            :pt="textareaPt"
            @update:modelValue="handleModelUpdate"
        >
            <slot />
        </Textarea>

        <!-- Text input (default) -->
        <InputText v-else v-bind="mappedProps" ref="input" :pt="inputTextPt" @update:modelValue="handleModelUpdate">
            <slot />
        </InputText>
        <small
            v-if="hasMaxLength"
            class="tw:text-gray-400 tw:text-xs tw:block tw:text-right tw:mt-1 tw:absolute tw:right-0"
        >{{ currentLength }}/{{ maxLength }}</small>
    </template>
</template>

<script>
import { mapInputPropsToPrimeVue } from './utils/componentMappings.js';
/**
 * EInput - Abstraction wrapper around PrimeVue input components
 *
 * Usage:
 *   <e-input v-model="value" type="text" placeholder="Enter text" />
 *   <e-input v-model="password" type="password" password-reveal />
 *   <e-input v-model="number" type="number" :min="0" :max="100" />
 *   <e-input v-model="text" type="textarea" />
 *   <e-input v-model="search" icon="search" placeholder="Search..." />
 *   <e-input v-model="clearable" icon-right="times" icon-right-clickable @icon-right-click="clear" />
 *
 * Buefy Props (work in both modes):
 *   - type: text, email, password, number, textarea, search, tel, url
 *   - placeholder: String
 *   - disabled: Boolean
 *   - required: Boolean
 *   - maxlength: Number
 *   - minlength: Number
 *   - password-reveal: Boolean (for password type)
 *   - icon: String (icon name for left side)
 *   - icon-right: String (icon name for right side)
 *   - icon-right-clickable: Boolean (makes right icon clickable)
 *   - size: is-small, is-medium, is-large
 *
 * Number-specific props:
 *   - min: Number
 *   - max: Number
 *   - step: Number
 *
 * PrimeVue-specific props:
 *   - fluid: Boolean (full width)
 *   - variant: String (outlined, filled)
 *
 * Property Mappings (Buefy → PrimeVue):
 *   - type="number" → InputNumber component
 *   - type="textarea" → Textarea component
 *   - type="password" → Password component
 *   - other types → InputText component
 *   - maxlength → maxLength
 *   - size → size (is-small/is-large → small/large)
 *   - icon → InputGroupAddon with left icon (wraps in InputGroup)
 *   - icon-clickable → Enables click even on left icon
 *   - icon-right → InputGroupAddon with right icon (wraps in InputGroup)
 *   - icon-right-clickable → Enables click event on right icon
 *
 * Events:
 *   - icon-click: Emitted when left icon is clicked (requires icon-clickable)
 *   - icon-right-click: Emitted when right icon is clicked (requires icon-right-clickable)
 */
export default {
    name: 'EInput',
    inheritAttrs: false,
    props: {
        // When true, renders icon addons but doesn't wrap in InputGroup
        // Used when parent component (like EField) is already handling InputGroup
        inGroup: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        expanded: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    computed: {
        inputType() {
            return this.$attrs.type || 'text';
        },

        hasPasswordReveal() {
            return this.$attrs['password-reveal'] !== undefined;
        },

        // Icon properties
        hasIcon() {
            return !!this.$attrs.icon;
        },

        hasValue() {
            const val = this.$attrs.modelValue ?? this.$attrs.value;
            return val !== null && val !== undefined && val !== '';
        },

        showClearButton() {
            return this.clearable && this.hasValue;
        },

        hasIconRight() {
            return !!this.$attrs['icon-right'] || this.showClearButton;
        },

        iconLeft() {
            if (this.$attrs.icon?.includes('pi')) {
                return this.$attrs.icon;
            }
            return this.$attrs.icon ? `uil uil-${this.$attrs.icon}` : undefined;
        },

        iconRight() {
            if (this.showClearButton) {
                return 'pi pi-times-circle';
            }
            if (this.$attrs['icon-right']?.includes('pi')) {
                return this.$attrs['icon-right'];
            }
            return this.$attrs['icon-right'] ? `uil uil-${this.$attrs['icon-right']}` : undefined;
        },

        isIconClickable() {
            return !!this.$attrs['icon-clickable'];
        },

        isIconRightClickable() {
            return !!this.$attrs['icon-right-clickable'] || this.showClearButton;
        },

        // Character counter properties
        maxLength() {
            return this.$attrs.maxlength ?? this.$attrs.maxLength ?? null;
        },

        hasMaxLength() {
            return this.maxLength !== null && this.maxLength !== undefined;
        },

        currentLength() {
            const value = this.$attrs.modelValue ?? '';
            return String(value).length;
        },

        mappedProps() {
            // Use shared mapping utility
            const values = mapInputPropsToPrimeVue(this.$attrs);

            // Only set min/max if they're valid numbers - delete entirely otherwise
            // PrimeVue InputNumber treats undefined/null as NaN
            if (values.min !== undefined && values.min !== null) {
                const parsed = Number(values.min);
                if (Number.isNaN(parsed)) {
                    delete values.min;
                } else {
                    values.min = parsed;
                }
            } else {
                delete values.min;
            }

            if (values.max !== undefined && values.max !== null) {
                const parsed = Number(values.max);
                if (Number.isNaN(parsed)) {
                    delete values.max;
                } else {
                    values.max = parsed;
                }
            } else {
                delete values.max;
            }

            return values;
        },

        // PassThrough configurations for styling internal elements
        inputTextPt() {
            const defaultPt = {
                root: {
                    class: 'placeholder:tw:text-gray-400 placeholder:tw:opacity-40 tw:truncate',
                },
            };
            return this.mergePt(defaultPt);
        },

        inputNumberPt() {
            const defaultPt = {
                input: {
                    class: 'placeholder:tw:text-gray-400 placeholder:tw:opacity-40 tw:truncate',
                },
            };
            return this.mergePt(defaultPt);
        },

        passwordPt() {
            const defaultPt = {
                input: {
                    class: 'placeholder:tw:text-gray-400 placeholder:tw:opacity-40 tw:truncate',
                },
            };
            return this.mergePt(defaultPt);
        },

        textareaPt() {
            const defaultPt = {
                root: {
                    class: 'placeholder:tw:text-gray-400 placeholder:tw:opacity-40',
                },
            };
            return this.mergePt(defaultPt);
        },

        // Expose commonly used properties from the underlying input
        $el() {
            return this.$refs.input?.$el;
        },
    },
    emits: ['icon-right-click', 'icon-click', 'clear', 'update:modelValue'],
    methods: {
        handleModelUpdate(value) {
            this.$emit('update:modelValue', value);
        },

        // Merge custom pt prop with default pt
        mergePt(defaultPt) {
            const customPt = this.$attrs.pt;
            if (!customPt) {
                return defaultPt;
            }

            // Deep merge pt objects
            const merged = { ...defaultPt };

            Object.entries(customPt).forEach(([key, value]) => {
                if (typeof value === 'object' && value !== null) {
                    merged[key] = {
                        ...defaultPt[key],
                        ...value,
                        // Merge class strings if both exist
                        class: [defaultPt[key]?.class, value?.class].filter(Boolean).join(' ') || undefined,
                    };
                } else {
                    merged[key] = value;
                }
            });

            return merged;
        },

        // Handle let icon click events
        handleIconClick() {
            if (this.showClearButton) {
                this.$emit('update:modelValue', '');
                this.$emit('clear');
                return;
            }
            if (this.isIconClickable) {
                this.$emit('icon-click');
            }
        },

        // Handle icon-right click events
        handleIconRightClick() {
            if (this.showClearButton) {
                this.$emit('update:modelValue', '');
                this.$emit('clear');
                return;
            }
            if (this.isIconRightClickable) {
                this.$emit('icon-right-click');
            }
        },

        // Expose commonly used methods from the underlying input
        focus() {
            this.$refs.input?.focus?.();
        },
        checkHtml5Validity() {
            return this.$refs.input?.checkHtml5Validity?.();
        },
    },
};
</script>
